import { Box, Flex, Text, IconButton, List, ListItem } from '@chakra-ui/react';
import type { IconProps } from '@chakra-ui/react';
import {
  PhosphorCheckIcon,
  PhosphorInfoIcon,
  PhosphorWarningIcon,
  PhosphorWarningCircleIcon,
  PhosphorXIcon,
} from '../theme/icons/phosphor';

export type ToastStatus =
  | 'success'
  | 'unsuccessful'
  | 'info'
  | 'error'
  | 'warning';

export type ToastDescription = string | string[] | false | null;

export interface CustomToastProps {
  onClose?: () => void;
  status: ToastStatus;
  title: string;
  description?: ToastDescription;
  width?: string;
}

const iconColours: Record<ToastStatus, string> = {
  success: 'success',
  unsuccessful: 'dangerRed',
  info: 'blueLight',
  error: 'error',
  warning: 'warning',
};

const descriptionLength = (description?: ToastDescription): number => {
  if (Array.isArray(description)) {
    return description.join(' ').length;
  }
  if (typeof description === 'string') {
    return description.length;
  }
  return 0;
};

const CustomToast = ({
  onClose,
  status,
  title,
  description,
  width: widthProp,
}: CustomToastProps) => {
  const isWide = descriptionLength(description) > 40 || title.length > 32;
  const width = widthProp ?? (isWide ? '600px' : '360px');

  const renderIcon = (toastStatus: ToastStatus) => {
    const iconProps: IconProps = {
      boxSize: '26px',
      flexShrink: 0,
      color: iconColours[toastStatus],
    };

    switch (toastStatus) {
      case 'success':
        return (
          <PhosphorCheckIcon data-testid="custom-check-icon" {...iconProps} />
        );
      case 'unsuccessful':
        return <PhosphorXIcon data-testid="custom-close-icon" {...iconProps} />;
      case 'info':
        return (
          <PhosphorInfoIcon data-testid="custom-info-icon" {...iconProps} />
        );
      case 'error':
        return (
          <PhosphorWarningCircleIcon
            data-testid="custom-exclamation-mark-icon"
            {...iconProps}
          />
        );
      case 'warning':
        return (
          <PhosphorWarningIcon
            data-testid="custom-exclamation-mark-triangle-icon"
            {...iconProps}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      boxShadow="0px 20px 40px 0px rgba(0, 0, 0, 0.15)"
      bg="white"
      w={{ base: '100%', md: width }}
      px="16px"
      py="12px"
      borderRadius="12px"
      color="secondaryDark"
      fontFamily="Inter Regular, sans-serif"
    >
      <Flex alignItems="flex-start" gap="8px">
        <Flex alignItems="center" h="26px" flexShrink={0}>
          {renderIcon(status)}
        </Flex>
        <Box flex="1" minW={0}>
          <Flex alignItems="flex-start" minH="26px">
            <Text
              fontFamily="Inter Medium, sans-serif"
              fontSize="16px"
              lineHeight="26px"
              wordBreak="break-word"
              flex="1"
              minW={0}
            >
              {title}
            </Text>
            <IconButton
              aria-label="Close"
              icon={<PhosphorXIcon boxSize="18px" />}
              onClick={onClose}
              variant="ghost"
              color="grey"
              flexShrink={0}
              boxSize="26px"
              minW="26px"
              p={0}
            />
          </Flex>
          {Array.isArray(description) ? (
            <List spacing="2px" textAlign="left" mt="4px">
              {description.map((msg, i) => (
                <ListItem
                  key={i}
                  textStyle="bodyFamilyMedium"
                  fontSize="14px"
                  wordBreak="break-word"
                >
                  &bull; {msg}
                </ListItem>
              ))}
            </List>
          ) : description ? (
            <Text
              textStyle="bodyFamilyMedium"
              fontSize="14px"
              lineHeight="20px"
              wordBreak="break-word"
              mt="4px"
            >
              {description}
            </Text>
          ) : null}
        </Box>
      </Flex>
    </Box>
  );
};

export default CustomToast;
